import { useState } from 'react';
import { Field, Input, Select } from '@/components/ui';
import { useVideoAction, videoApi } from '@/features/video-consults/api';
import { money } from '@/features/video-consults/format';
import type {
  StaffVideoConsultRequest,
  TeleconsultType,
} from '@/features/video-consults/types';
import { FormDialog } from '@/pages/clinical/FormDialog';
import parity from '@/pages/ops/parity.module.css';
import { TriageSummary } from './TriageSummary';
import styles from './video.module.css';

/** Accept: a teleconsult slot of your own, its video session and the fee bill. */
export function AcceptRequestDialog({
  r,
  onClose,
}: {
  r: StaffVideoConsultRequest;
  onClose: () => void;
}) {
  const a = useVideoAction(videoApi.accept);
  const [when, setWhen] = useState('');
  const [type, setType] = useState<TeleconsultType>('follow_up');
  const [payLater, setPayLater] = useState(false);
  return (
    <FormDialog
      title={`Accept video consult · ${r.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={!when}
      submitLabel="Accept"
      onSubmit={() =>
        a.mutateAsync({
          id: r.id,
          version: r.version,
          startsAt: new Date(`${when}:00+08:00`).toISOString(),
          type,
          payLater,
        })
      }
    >
      <TriageSummary triage={r.triage} />
      <p className={parity.sub}>
        Fee {money(r.price.youPaySen)}
        {r.payer ? ` · ${r.payer.name} pays` : ' · paid at the counter'}. The
        patient gets a notification that the consult is booked.
      </p>
      <Field label="Consult time">
        <Input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
        />
      </Field>
      <Field label="Type">
        <Select
          value={type}
          onChange={(e) => setType(e.target.value as TeleconsultType)}
          options={[
            { value: 'follow_up', label: 'Follow-up · existing patient' },
            { value: 'first_visit', label: 'First visit by video' },
          ]}
        />
      </Field>
      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={payLater}
          onChange={(e) => setPayLater(e.target.checked)}
        />
        Let the patient join before paying (the bill stays open at the counter)
      </label>
    </FormDialog>
  );
}

/** Decline: the reason is shown to the patient in the app. */
export function DeclineRequestDialog({
  r,
  onClose,
}: {
  r: StaffVideoConsultRequest;
  onClose: () => void;
}) {
  const a = useVideoAction(videoApi.decline);
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  return (
    <FormDialog
      title={`Decline video consult · ${r.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={trimmed.length < 5}
      submitLabel="Decline"
      onSubmit={() =>
        a.mutateAsync({ id: r.id, version: r.version, reason: trimmed })
      }
    >
      <Field
        label="Reason for the patient"
        hint="Shown to the patient in the app: patient-safe wording, 5–300 characters."
      >
        <textarea
          className={styles.textarea}
          rows={3}
          maxLength={300}
          value={reason}
          placeholder="e.g. Please come to the clinic so a doctor can examine you."
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}
